import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, Globe, Phone, Loader2, ArrowRight } from 'lucide-react';
import { guessCompanyName, guessWebsiteUrl } from '../utils/domainUtils';
import { queueDomainForProcessing } from '../services/backgroundJobService';
import { clientLogger } from '../utils/logger';

/**
 * Organization form that collects company details and handles domain pre-population
 */
const OrganizationForm = ({ onSuccess }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const domainFromUrl = searchParams.get('domain');
    
    const [formData, setFormData] = useState({
        name: '',
        website: '',
        phone: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [domain, setDomain] = useState(domainFromUrl || '');

    // Pre-populate fields based on domain
    useEffect(() => {
        if (domain) {
            setFormData(prev => ({
                ...prev,
                name: prev.name || guessCompanyName(domain),
                website: prev.website || guessWebsiteUrl(domain)
            }));
        }
    }, [domain]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Get user ID from localStorage if available
            const userDataString = localStorage.getItem('userData');
            const userDataFromStorage = userDataString ? JSON.parse(userDataString) : null;
            const userIdFromStorage = userDataFromStorage?.id;
            
            // Log for debugging
            clientLogger.debug('Creating organization with user data from storage:', userDataFromStorage);
            
            // Extract domain from website if provided
            let extractedDomain = domain || '';
            if (formData.website && !extractedDomain) {
                try {
                    const websiteUrl = formData.website.startsWith('http') 
                        ? formData.website 
                        : `https://${formData.website}`;
                    const url = new URL(websiteUrl);
                    extractedDomain = url.hostname.replace(/^www\./, '');
                    clientLogger.debug('Extracted domain from website:', extractedDomain);
                } catch (err) {
                    clientLogger.warn('Could not extract domain from website URL:', err);
                }
            }
            
            const response = await fetch('/api/organizations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    name: formData.name,
                    website_url: formData.website,
                    phone_number: formData.phone,
                    domain: extractedDomain,
                    userId: userIdFromStorage // Send userId from localStorage
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to create organization');
            }

            const organization = await response.json();
            
            // Queue domain for processing if it was extracted
            if (extractedDomain && userIdFromStorage) {
                try {
                    clientLogger.debug('Queuing domain for processing:', extractedDomain);
                    await queueDomainForProcessing(extractedDomain, userIdFromStorage, organization.id);
                    clientLogger.debug('Successfully queued domain for processing');
                } catch (queueError) {
                    // Don't fail the organization creation if domain queuing fails
                    clientLogger.error('Failed to queue domain for processing:', queueError);
                }
            }
            
            if (onSuccess) {
                onSuccess(organization);
            } else {
                // Default navigation to the dashboard with the new org
                navigate(`/dashboard?organizationId=${organization.id}`);
            }
        } catch (error) {
            console.error('Organization creation error:', error);
            setError(error.message || 'Failed to create organization. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-green-50">
            {/* Background Pattern */}
            <div className="absolute inset-0 z-0 opacity-20" 
                style={{ 
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23065f46' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` 
                }}
            />

            <div className="relative w-full max-w-md px-4">
                {/* Logo Section */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-600 text-white text-2xl font-bold mb-4">
                        C360
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Company Details</h1>
                    <p className="mt-2 text-gray-600">Tell us about your organization</p>
                </div>

                <div className="backdrop-blur-md bg-white/70 p-8 rounded-2xl shadow-xl border border-white/20">
                    {error && (
                        <div className="mb-6 bg-red-50 border border-red-100 text-red-600 p-4 rounded-lg flex items-center text-sm">
                            <div className="w-2 h-2 bg-red-600 rounded-full mr-2" />
                            {error}
                        </div>
                    )}

                    <form 
                        onSubmit={handleSubmit} 
                        className="space-y-6 pt-4" 
                        method="post"
                        autoComplete="off"
                        spellCheck="false"
                    >
                        <div className="space-y-2">
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                Organization Name
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Building2 className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="block w-full pl-10 px-4 py-3 rounded-lg border border-gray-300 shadow-sm transition-colors
                                        focus:border-green-500 focus:ring-2 focus:ring-green-200 focus:outline-none
                                        placeholder:text-gray-400 text-gray-900 bg-white/80"
                                    placeholder="Enter your organization name"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                                Website URL
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Globe className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="website"
                                    name="website"
                                    type="text"
                                    value={formData.website}
                                    onChange={handleChange}
                                    className="block w-full pl-10 px-4 py-3 rounded-lg border border-gray-300 shadow-sm transition-colors
                                        focus:border-green-500 focus:ring-2 focus:ring-green-200 focus:outline-none
                                        placeholder:text-gray-400 text-gray-900 bg-white/80"
                                    placeholder="Enter your company website"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                Phone Number
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Phone className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="block w-full pl-10 px-4 py-3 rounded-lg border border-gray-300 shadow-sm transition-colors
                                        focus:border-green-500 focus:ring-2 focus:ring-green-200 focus:outline-none
                                        placeholder:text-gray-400 text-gray-900 bg-white/80"
                                    placeholder="Enter your phone number"
                                />
                            </div>
                        </div>

                        <div className="pt-4">
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="relative w-full flex items-center justify-center py-3 px-4 border border-transparent 
                                    rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 
                                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 
                                    disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                            >
                                {isLoading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        Continue to Dashboard
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default OrganizationForm; 